import debounce from "lodash/debounce";
import React, { ReactElement, useEffect, useState } from "react";
import mixpanel from "mixpanel-browser";
import { useDispatch, useSelector } from "react-redux";
import { Form, Formik, FormikProps } from "formik";
import moment from "moment";
import { createColumnHelper } from "@tanstack/react-table";
import { getManifests, manifestsSelector, resetErrors, resetManifests } from "../../slices/manifest/manifestSlice";
import ManifestsState from "../../slices/manifest/ManifestsState";
import { ManifestsRequest, ManifestsFilterSchema, ManifestsFilter } from "../../app/data/manifest/requestModels";
import { ManifestStatusProfile, ManifestTableRow, StopProfile } from "../../app/data/manifest/manifestProfiles";
import ManifestDetails from "./details/manifestDetails";
import { arrangeName } from "../../hooks/utils";
import ManifestsService from "../../app/data/manifest/manifestsService";
import { DriverSearch } from "../../app/data/manifest/models";
import { userSelector } from "../../slices/user/userSlice";
import UserState from "../../slices/user/UserState";
import ContentContainer from "../../templates/content-container/contentContainer";
import ContentContainerToolbar from "../../ui-components/molecules/content-container-toolbar/contentContainerToolbar";
import Table from "../../ui-components/table/table";
import Button, { ButtonThemes } from "../../ui-components/button/button";
import XGSErrorMessage from "../../ui-components/error-message/errorMessage";
import XGSFormDate from "../../ui-components/form/date/xgsFormDate";
import { LabelModes } from "../../ui-components/molecules/labeled-inputs/labeledInput";
import XGSFormSelect from "../../ui-components/form/select/xgsFormSelect";
import XGSFormInput from "../../ui-components/form/input/xgsFormInput";
import CompliancePercentage from "./list/compliancePercentage";
import DownloadButton from "../../ui-components/download-button/downloadButton";
import Legend from "../../ui-components/molecules/legend/legend";
import ProgressBar from "../../ui-components/progress-bar/progressBar";
import CompliancePercentageLegend from "./list/compliancePercentageLegend";
import { useDownload } from "../../hooks/useDownload";
import "./manifestsView.scss";
import { PAGE_IDS } from "../../app/data/common/pageIds";

const manifestService = ManifestsService.getInstance();

const columnHelper = createColumnHelper<any>();

export const ManifestsView: React.FC = () => {
  const dispatch = useDispatch();
  const manifestsState: ManifestsState = useSelector(manifestsSelector);
  const userState: UserState = useSelector(userSelector);
  const [manifest, setManifest] = useState<ManifestTableRow | null>(null);
  const [lastFilter, setLastFilter] = useState<ManifestsRequest | null>(null);
  const [filterError, setFilterError] = useState<ReactElement | null>(null);
  const formRef = React.useRef<FormikProps<ManifestsFilter>>(null);

  const columns = [
    columnHelper.accessor("manifestNumber", {
      id: "manifestNumber",
      header: "Manifest",
      cell: (cellProps) => (<>{cellProps.getValue()}</>),
      size: 96,
    }),
    columnHelper.accessor("driverName", {
      id: "driverName",
      header: "Driver",
      cell: (cellProps) => {
        return (
          <div className="cell__driver">
            {arrangeName(cellProps.getValue())}
            <span>({cellProps.row.original.driverEmployeeNumber})</span>
          </div>
        )
      },
      size: 127,
    }),
    columnHelper.accessor("trailerNumber", {
      id: "trailerNumber",
      header: "Trailer",
      cell: (cellProps) => (<>{cellProps.getValue()}</>),
      size: 87,
    }),
    columnHelper.accessor("dispatchedTs", {
      id: "dispatchedTs",
      header: "Dispatched At",
      cell: (cellProps) => {
        const date = moment(cellProps.getValue(), "MMM D YYYY hh:mm");
        return (
          <div className="cell__driver">
            {date.format("MMM D, YYYY")}
            <div className="text-light">{date.format("h:mm A")}</div>
          </div>
        )
      },
      size: 87,
    }),
    columnHelper.accessor("probills", {
      id: "probills",
      header: "Deliveries",
      cell: (cellProps) => (<>{cellProps.getValue()}</>),
      size: 80,
    }),
    columnHelper.accessor((row) => {
      if (!manifestsState?.manifestsPickups?.[row.manifestNumber]) return 0;

      return manifestsState.manifestsPickups[row.manifestNumber].length;
    }, {
      id: "pickups",
      header: "Pickups",
      cell: (cellProps) => (<>{cellProps.getValue()}</>),
      size: 80,
    }),
    columnHelper.accessor("items", {
      id: "items",
      header: "Items",
      cell: (cellProps) => (<>{cellProps.getValue()}</>),
      size: 80,
    }),
    columnHelper.accessor("exceptions", {
      id: "exceptions",
      header: "Exceptions",
      cell: (cellProps) => (<span className={"cell__exc" + (cellProps.getValue() > 0 ? " --alert" : "")}>{cellProps.getValue()}</span>),
      size: 87,
    }),
    columnHelper.display({
      id: "progress",
      header: "Progress",
      cell: (cellProps) => {
        let thisStops: StopProfile[] = []
        if (manifestsState.manifestsStops && manifestsState.manifestsStops[cellProps?.row?.original?.manifestNumber]) {
          thisStops = Object.values(manifestsState.manifestsStops[cellProps?.row?.original?.manifestNumber])
        }
        let completedStopsCount = 0;
        let progressFloatValue = 0;

        if (thisStops && thisStops.length > 0) {
          thisStops.forEach(stop => stop.stopStatus === "complete" && completedStopsCount++)
        }

        const stopsCompleted = (completedStopsCount > -1 ? completedStopsCount : cellProps.row.original.stops) + "/" + cellProps.row.original.stops

        if (completedStopsCount === -1) {
          progressFloatValue = 100
        } else if (completedStopsCount > 0) {
          progressFloatValue = Math.floor((completedStopsCount / cellProps.row.original.stops) * 100)
        }

        return <ProgressBar
          percentage={progressFloatValue}
          additionalInfo={`${stopsCompleted} stops`}
          multicolor
        />;
      },
      size: 147,
    }),
    columnHelper.accessor((row) => {
      if (!manifestsState?.manifestsProbills?.[row.manifestNumber]) return 0;

      const probills = Object.values(manifestsState.manifestsProbills[row.manifestNumber]);

      const driverAck = probills.filter(probill => !!probill.driverAckDate);

      const percentage = (driverAck.length * 100) / probills.length;

      return percentage;
    }, {
      id: "compliance",
      header: () => (
        <div className="xgs-table__cell-content--centered">
          {"Compliance percentage"}
          <Legend mix="xgs-table__tooltip">
            <CompliancePercentageLegend />
          </Legend>
        </div>
      ),
      cell: (cellProps) => (
        <div className="xgs-table__cell-content--centered">
          <CompliancePercentage
            id={cellProps?.row?.original.manifestNumber}
            percentage={cellProps.getValue()}
            isCompleted={cellProps?.row?.original.pending === 0} />
        </div>
      ),
      size: 96,
    }),
  ];

  const terminalRequired = (data: ManifestsFilter) => (!data.manifestNumber && !data.probillNumber && !data.driverId);

  const searchParams = (data: ManifestsRequest): ManifestsRequest => {
    const { dispatchedDate, terminal, ...rest } = data;

    return {
      dispatchedDate: dispatchedDate && dispatchedDate.toCustomDateFormat("YYYYMMDD"),
      ...terminalRequired(data) && { terminal },
      ...rest,
    };
  }

  const searchManifests = (data: ManifestsRequest) => {
    dispatch(getManifests(searchParams(data)));
    setLastFilter(data);
  };

  const onSubmit = async (data: ManifestsFilter) => {
    setFilterError(null);
    setManifest(null);
    resetDownload();

    const terminal = userState.activeTerminal?.id;

    if (!terminal && terminalRequired(data)) {
      setFilterError(
        <div>
          You do not have assigned terminals.
          Email <a className="white-link" href="mailto:helpdesk@xgsi.com">helpdesk@xgsi.com</a> to request terminal assignment for your account.
        </div>
      );
    } else {
      searchManifests({ ...data, terminal });
    }
  };

  const fetchCSV = () => manifestService.exportManifestsCSV(searchParams(lastFilter || {}));

  const {
    download,
    resetDownload,
    loadingState
  } = useDownload(
    "MANIFESTS",
    fetchCSV,
  );

  useEffect(() => {
    return () => {
      dispatch(resetManifests())
      dispatch(resetErrors())
      setManifest(null)
    };
  }, [dispatch]);

  useEffect(() => {
    mixpanel.track("page_viewed", { page: "manifest_tracking" });
    if (userState.activeTerminal?.id) {
      formRef.current?.submitForm();
    }
    // eslint-disable-next-line
  }, [userState.activeTerminal?.id]);

  // eslint-disable-next-line
  const loadDriverOptions = React.useCallback(
    debounce((query, callback) => {
      manifestService.getDriversSearch(query)
        .then(options => callback(options.map(({ driver, searchDetails }: DriverSearch) => ({
          value: driver.driverId,
          label: <span dangerouslySetInnerHTML={{ __html: searchDetails.matchedStr }} />
        }))))
    }, 300),
    []
  );

  const error = (manifestsState.fetch_was_failed && manifestsState.fetch_fail_reason) || filterError;

  const sortedManifests = Object.values(manifestsState.manifestsStatuses)
    .sort((a, b) => {
      const aTime = new Date(a.dispatchedTs).getTime();
      const bTime = new Date(b.dispatchedTs).getTime();
      const aValid = !isNaN(aTime);
      const bValid = !isNaN(bTime);
      if (aValid && bValid) {
        return bTime - aTime;
      } else if (aValid) {
        return -1;
      } else if (bValid) {
        return 1;
      } else {
        return 0;
      }
    });


  return (
    <ContentContainer
      className="xgs-list xgs-list--full-width"
      mods={{ 'full-width': true }}
      titleComponent={
        <ContentContainerToolbar
          pageId={PAGE_IDS.TRACK_MANIFESTS}
          title="Manifests"
          mods={{ "full-width": true }}
        >
          <DownloadButton
            title="Download CSV"
            spinner={loadingState?.downloadStarted}
            disabled={!lastFilter || !manifestsState.manifestsStatuses.length}
            onClick={() => {
              !loadingState?.downloadStarted && download();
            }}
          />
        </ContentContainerToolbar>
      }
    >
      <div className="xgs-manifests-view">
        <div className="xgs-list__controls">
          <div className="xgs-list__controls__search">
            <Formik
              initialValues={{
                dispatchedDate: new Date().toString(),
                manifestNumber: '',
                probillNumber: "",
                driverId: "",
              }}
              onSubmit={onSubmit}
              validationSchema={ManifestsFilterSchema}
              innerRef={formRef}
            >
              {(props: FormikProps<ManifestsFilter>) => (
                <Form className="xgs-list__controls__search-form">
                  <XGSFormDate
                    name="dispatchedDate"
                    label="Dispatched Date"
                    className="xgs-item-form__field"
                    onChange={() => ''}
                    onDateChange={(v) => props.setFieldValue("dispatchedDate", v)}
                  />
                  <XGSFormInput
                    type="text"
                    name="manifestNumber"
                    label="Manifest"
                    labelMode={LabelModes.column}
                    className="xgs-item-form__field"
                  />
                  <XGSFormInput
                    type="text"
                    name="probillNumber"
                    label="Probill"
                    labelMode={LabelModes.column}
                    className="xgs-item-form__field"
                  />
                  <XGSFormSelect
                    name="driverId"
                    label="Driver"
                    labelMode={LabelModes.column}
                    placeholder="Driver name or number"
                    isClearable
                    async
                    loadOptions={loadDriverOptions}
                    options={[]} // default options    
                    onValueChange={(t) => props.setFieldValue("driverId", t?.value)}
                    className="xgs-item-form__field"
                  />
                  <Button
                    type="submit"
                    theme={ButtonThemes.blue}
                    className=""
                    spinner={false}
                  >
                    Search
                  </Button>
                </Form>
              )}
            </Formik>
          </div>
        </div>
        {!!error ? (
          <XGSErrorMessage>{error}</XGSErrorMessage>
        ) : (
          <div className="xgs-list__table-wrapper">
            <Table
              isLoading={manifestsState.fetch_was_started && manifestsState.requestCreator === "GET_MANIFESTS"}
              columns={columns}
              data={sortedManifests}
              cursorPointer={true}
              rowHeight={64}
              noResultsText="There are no matching manifests"
              onRowClicked={(row: ManifestStatusProfile) => {
                setManifest(row)
              }}
              responsive={true}
            />
          </div>
        )}

        {manifest && (
          <ManifestDetails
            manifest={manifest}
            show={!!manifest?.manifestNumber}
            onClose={() => setManifest(null)}
          />
        )}
      </div>
    </ContentContainer>
  )
};